import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import documentAPI from "../document/documentAPI";
import journalAPI from "./journalAPI";
import KPConfig from '../../KPConfig.js'
import { sanitizeFilenameInUrl } from '../../utils/StringSanitizer';
import { getGlobalAbortController } from "../../utils/abortController";

const initialState = {
  status: 'idle',
  downloadJournalStatus: 'idle',
  downloadJournalWeekStatus: 'idle',
  getDeletedJournalEntriesStatus: 'idle',
  deleteJournalPageStatus: 'idle',
  restoreDeletedJournalEntry: {
    status: 'idle',
    entryId: null
  },
  journal: {},
  journalEntries: [],
  deletedJournalEntries: [],
  sortedJournalEntries: [],
  journalPDFUrl: ""
}

export const getJournal = createAsyncThunk (
  'journal/getJournal',
  async (params) => {
    const response = await journalAPI.getJournal(params)
    return response.body
  }
)

export const getJournalEntries = createAsyncThunk (
  'journal/getJournalEntries',
  async (params) => {
    const response = await journalAPI.getJournalEntries(params)
    return response.body
  }
)

export const createNewJournal = createAsyncThunk (
  'journal/createNewJournal',
  async (params) => {
    const response = await journalAPI.newCreateNewJournal(params.parent_id, params.title, params.template_id)
    return response.body
  }
)

export const addNewJournalEntry = createAsyncThunk (
  'journal/addNewJournalEntry',
  async (params) => {
    const response = await journalAPI.newAddNewJournalEntry(params.journalId, params.copyPrevious)
    return response.body
  }
)

export const deleteJournal = createAsyncThunk (
  'journal/deleteJournal',
  async (params) => {
    const response = await journalAPI.deleteJournal(params)
    return response.body
  }
)

export const deleteJournalPage = createAsyncThunk (
  'journal/deleteJournalPage',
  async (params) => {
    const response = await journalAPI.deleteJournalPage(params)
    return response?.body
  }
);

export const getDeletedJournalEntries = createAsyncThunk (
  'journal/getDeletedJournalEntries',
  async (params) => {
    const response = await journalAPI.getDeletedJournalEntries(params)
    return response.body
  }
)

export const restoreDeletedJournalEntry = createAsyncThunk(
  'journal/restoreDeletedJournalEntry',
  async (documentId, { dispatch, getState }) => {
    const response = await journalAPI.restoreDeletedJournalEntry(documentId)

    // Refresh journal entries after successful restore
    const state = getState()
    const objectId = state.journal.journal?.object?.id
    if (objectId) {
      dispatch(getJournalEntries(objectId))
    }

    return response.body
  }
)

export const downloadJournalPdf = createAsyncThunk(
  'journal/downloadJournalPdf',
  async (params) => {

    let ts = params.ts * 1000
    const formattedDate = new Intl.DateTimeFormat('fi-FI', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(ts).replaceAll('.', '-');
    
    const documentName = sanitizeFilenameInUrl(`${params.documentId} ${params.objectTitle} ${formattedDate}.pdf`);
    const globalAbortController = getGlobalAbortController();

    const response1 = await documentAPI.getDocumentStatusId(params.documentId, 'pdf', 'pdf', globalAbortController);

    if (!response1) {
      return;
    }

    if (response1.statusCode !== 200) {
      throw new Error('Error getting journal status');
    }

    const { status_id } = response1.body || {};

    if (!status_id) {
      throw new Error('No status ID for PDF journal');
    }

    const processedDownloadId = await documentAPI.getPdfProcessedDownloadId(response1.body.status_id, globalAbortController);

    //We don't want to throw error if user has canceled the download
    if (processedDownloadId === "AbortError") {
      return;
    }

    if (!processedDownloadId) {
      throw new Error('No processedDownloadId for PDF journal');
    }

    let url = `${KPConfig.backendUrl}/pdf/download/${processedDownloadId}/${documentName}`;

    return url;
  }
);

export const downloadJournalWeekPdf = createAsyncThunk(
  'journal/downloadJournalWeekPdf',
  async (params) => {
    const documentPdfName = sanitizeFilenameInUrl(`${params.objectTitle}_${params.weekIdentifier}.pdf`);
    const globalAbortController = getGlobalAbortController();

    // Call new backend endpoint with comma-separated document IDs
    const response = await documentAPI.getDocumentStatusId(params.documentIds, 'pdf', 'pdf', globalAbortController);

    if (!response) {
      return;
    }

    if (response.statusCode !== 200) {
      throw new Error('Error getting journal week PDF status');
    }

    const { status_id } = response.body || {};

    if (!status_id) {
      throw new Error('No status ID for journal week PDF');
    }

    const processedDownloadId = await documentAPI.getPdfProcessedDownloadId(status_id, globalAbortController);

    // We don't want to throw error if user has canceled the download
    if (processedDownloadId === "AbortError") {
      return;
    }

    if (!processedDownloadId) {
      throw new Error('No processedDownloadId for journal week PDF');
    }

    const url = `${KPConfig.backendUrl}/pdf/download/${processedDownloadId}/${documentPdfName}`;
    await documentAPI.downloadDocument(url, documentPdfName);

    return { success: true };
  }
);

export const journalSlice = createSlice({
  name: 'journal',
  initialState,
  reducers: {
    addSortedEntries(state, action) {
      state.sortedJournalEntries = action.payload
    },
    resetJournal: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJournal.fulfilled, (state, action) => {
        state.journal = action.payload
      })

      .addCase(getJournalEntries.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(getJournalEntries.fulfilled, (state, action) => {
        if (action.payload) {
          state.journalEntries = action.payload.results
        }
        state.status = 'idle'
      })
      .addCase(getJournalEntries.rejected, (state) => {
        state.status = 'failed'
      })

      .addCase(createNewJournal.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(createNewJournal.fulfilled, (state) => {
        state.status = 'idle'
      })
      .addCase(createNewJournal.rejected, (state) => {
        state.status = 'failed'
      })

      .addCase(addNewJournalEntry.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(addNewJournalEntry.fulfilled, (state, action) => {
        state.journal = action.payload
        state.status = 'idle'
      })
      .addCase(addNewJournalEntry.rejected, (state) => {
        state.status = 'failed'
      })

      .addCase(deleteJournal.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(deleteJournal.fulfilled, (state, action) => {
        state.journal = action.payload
        state.status = 'idle'
      })
      .addCase(deleteJournal.rejected, (state) => {
        state.status = 'failed'
      })

      .addCase(deleteJournalPage.pending, (state) => {
        state.deleteJournalPageStatus = 'loading'
      })
      .addCase(deleteJournalPage.fulfilled, (state) => {
        state.deleteJournalPageStatus = 'idle'
      })
      .addCase(deleteJournalPage.rejected, (state) => {
        state.deleteJournalPageStatus = 'failed'
      })

      .addCase(getDeletedJournalEntries.pending, (state) => {
        state.getDeletedJournalEntriesStatus = 'loading';
      })
      .addCase(getDeletedJournalEntries.fulfilled, (state, action) => {
        if (action.payload) {
          state.deletedJournalEntries = action.payload.results;
        }
        state.getDeletedJournalEntriesStatus = 'idle';
      })
      .addCase(getDeletedJournalEntries.rejected, (state) => {
        state.getDeletedJournalEntriesStatus = 'error';
      })

      .addCase(restoreDeletedJournalEntry.pending, (state, action) => {
        state.restoreDeletedJournalEntry.status = 'loading';
        state.restoreDeletedJournalEntry.entryId = action.meta.arg;
      })
      .addCase(restoreDeletedJournalEntry.fulfilled, (state, action) => {
        state.restoreDeletedJournalEntry.status = 'idle';
        state.restoreDeletedJournalEntry.entryId = null;
        if (action.payload) {
          state.deletedJournalEntries = state.deletedJournalEntries.filter(entry => entry.id !== action.payload.id);
        }
      })
      .addCase(restoreDeletedJournalEntry.rejected, (state) => {
        state.restoreDeletedJournalEntry.status = 'error';
        state.restoreDeletedJournalEntry.entryId = null;
      })

      // downloadPdf
      .addCase(downloadJournalPdf.pending, (state) => {
        state.downloadJournalStatus = 'loading';
        state.journalPDFUrl = "";
      })
      .addCase(downloadJournalPdf.fulfilled, (state, action) => {
        state.downloadJournalStatus = 'idle';
        state.journalPDFUrl = action.payload;
      })
      .addCase(downloadJournalPdf.rejected, (state) => {
        state.downloadJournalStatus = 'error';
      })

      // download weekly compilation Pdf
      .addCase(downloadJournalWeekPdf.pending, (state) => {
        state.downloadJournalWeekStatus = 'loading';
      })
      .addCase(downloadJournalWeekPdf.fulfilled, (state) => {
        state.downloadJournalWeekStatus = 'idle';
      })
      .addCase(downloadJournalWeekPdf.rejected, (state) => {
        state.downloadJournalWeekStatus = 'idle';
      })
  }
})

export const selectJournal = (state) => state.journal.journal;
export const selectDownloadJournalStatus = (state) => state.journal.downloadJournalStatus;
export const selectDownloadJournalWeekStatus = (state) => state.journal.downloadJournalWeekStatus;
export const selectDeleteJournalPageStatus = (state) => state.journal.deleteJournalPageStatus;
export const selectJournalPDFUrl = (state) => state.journal.journalPDFUrl;
export const selectDeletedJournalEntries = (state) => state.journal.deletedJournalEntries;
export const selectGetDeletedJournalEntriesStatus = (state) => state.journal.getDeletedJournalEntriesStatus;
export const selectRestoreDeletedJournalEntry = (state) => state.journal.restoreDeletedJournalEntry;

export const {resetJournal, addSortedEntries} = journalSlice.actions
export default journalSlice.reducer